import { ForwardedRef, useImperativeHandle, useRef, useState } from 'react';
import YouTube from 'react-youtube';
import { ValuesType } from 'utility-types';

import { seconds } from '~/interfaces';
import { getMasterGain } from '~/modules/audio/master-volume';
import { VideoState } from '~/modules/elements/video-player/video-state';
import usePlayerVolume from '~/modules/hooks/use-player-volume';
import useUnstuckYouTubePlayer from '~/modules/hooks/use-unstuck-you-tube-player';

const stateMap = {
  [YouTube.PlayerState.UNSTARTED]: VideoState.UNSTARTED,
  [YouTube.PlayerState.ENDED]: VideoState.ENDED,
  [YouTube.PlayerState.PLAYING]: VideoState.PLAYING,
  [YouTube.PlayerState.PAUSED]: VideoState.PAUSED,
  [YouTube.PlayerState.BUFFERING]: VideoState.BUFFERING,
  [YouTube.PlayerState.CUED]: VideoState.CUED,
} as const;

interface Props {
  video: string;
  autoplay?: boolean;
  controls?: boolean;
  disablekb?: boolean;
  volume?: number;
  startAt?: number;
  onStateChange?: (state: VideoState) => void;
  /** The player finished its API handshake. Commands sent before this (loadVideoById, playVideo…)
   * are posted to an iframe that isn't listening yet and are dropped, silently and for good. */
  onReady?: () => void;
  width: number;
  height: number;
  ref?: ForwardedRef<VideoPlayerRef>;
}

export interface LoadVideByIdOpts {
  videoId: string;
  startSeconds: number;
  endSeconds?: number;
}

export interface VideoPlayerRef {
  getStatus: () => ValuesType<typeof stateMap>;
  seekTo: (time: seconds) => void;
  setPlaybackSpeed: (speed: number) => void;
  setVolume: (newVolume: number) => void;
  getCurrentTime: () => Promise<seconds>;
  loadVideoById: (opts: LoadVideByIdOpts) => void;
  setSize: (w: number, h: number) => void;
  playVideo: () => void;
  pauseVideo: () => void;
  getDuration: () => Promise<number>;
}

export default function YoutubeVideoPlayer({
  video,
  autoplay,
  startAt,
  controls,
  disablekb,
  volume,
  width,
  height,
  onStateChange,
  onReady,
  ref,
}: Props) {
  const player = useRef<YouTube | null>(null);
  const [currentStatus, setCurrentStatus] = useState(YouTube.PlayerState.UNSTARTED);

  const playerKey = useUnstuckYouTubePlayer(player, currentStatus);
  const { requestedVolume, setRequestedVolume } = usePlayerVolume(player, volume);

  useImperativeHandle(ref, () => ({
    getStatus: () => stateMap[currentStatus],
    setSize: (w, h) => player.current?.getInternalPlayer()!.setSize(w, h),
    seekTo: (timeSec: seconds) => player.current?.getInternalPlayer()!.seekTo(timeSec, true),
    setPlaybackSpeed: (speed: number) => player.current?.getInternalPlayer()!.setPlaybackRate(Number(speed)),
    // 0-100 like the YouTube API; the master gain is applied by `usePlayerVolume`
    setVolume: (newVolume: number) => setRequestedVolume(newVolume / 100),
    getCurrentTime: () => player.current?.getInternalPlayer?.()?.getCurrentTime?.() ?? Promise.resolve(0),
    loadVideoById: (opts) => player.current?.getInternalPlayer()!.loadVideoById(opts),
    playVideo: () => {
      // console.log('play');
      player.current?.getInternalPlayer()!.playVideo();
    },
    pauseVideo: () => {
      // console.log('paused');
      return player.current?.getInternalPlayer()!.pauseVideo();
    },
    getDuration: () => player.current?.getInternalPlayer?.()?.getDuration?.() ?? Promise.resolve(0),
  }));

  return (
    <YouTube
      title=" "
      key={`${video}-${playerKey}`}
      ref={player}
      videoId={video}
      opts={{
        width: width,
        height: height,
        playerVars: {
          autoplay: autoplay ? 1 : 0,
          rel: 0,
          fs: 0,
          controls: controls ? 1 : 0,
          start: startAt ?? 0,
          end: 0,
          disablekb: disablekb ? 1 : 0,
          modestbranding: 1,
        },
      }}
      onReady={(e) => {
        // A (re)mounted player starts at full volume, so a lowered master level is applied right away
        const gain = getMasterGain();
        if (gain < 1) e.target.setVolume(Math.round(requestedVolume * gain * 100));
        onReady?.();
      }}
      onPlaybackRateChange={(e) => {
        console.log('onPlaybackRateChange', e.data);
      }}
      onStateChange={(e) => {
        setCurrentStatus(e.data);
        onStateChange?.(stateMap[e.data]);
      }}
    />
  );
}
